import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button, Stack, TooltipPopover } from 'src/core/ui';
import {
  CheckIcon,
  ExpandIcon,
  FoldIcon,
  InfoOutlineIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { RowInfo } from 'src/WebApiController';

import * as styles from './FilterPillAbsoluteInput.css';

type FilterPillAbsoluteInputProps = {
  sectionId: number;
  rowRange?: [number, number];
  sectionRows: RowInfo[];
  onUpdateRowRange: (
    sectionId: number,
    lowerRowName: string,
    upperRowName: string
  ) => void;
};
export const FilterPillAbsoluteInput = ({
  sectionId,
  rowRange,
  sectionRows,
  onUpdateRowRange,
}: FilterPillAbsoluteInputProps) => {
  const { isDarkMode } = useSiteTheme();
  const [showInput, setShowInput] = useToggle(false);
  const [lowerRowRange, setLowerRowRange] = useState<string>();
  const [upperRowRange, setUpperRowRange] = useState<string>();

  useEffect(() => {
    if (rowRange) {
      const [lower, upper] = rowRange;
      setLowerRowRange(lower + '');
      setUpperRowRange(upper + '');
      setShowInput(true);
    }
  }, [rowRange, setShowInput]);

  const fromText = useContent(ContentId.From);
  const toText = useContent(ContentId.To);

  const filteredRows = useMemo(
    () =>
      sectionRows.filter(
        (r) => r.name && !r.isSpeculative && r.ordinal != null
      ) ?? [],
    [sectionRows]
  );
  const idToOrdinal = useMemo(
    () =>
      filteredRows.reduce(
        (res, row) => {
          res[row.id] = row.ordinal!;
          return res;
        },
        {} as Record<string, number>
      ),
    [filteredRows]
  );
  const valueOptionsContent = useMemo(
    () =>
      filteredRows
        .toSorted((a, b) => {
          return idToOrdinal[a.id] - idToOrdinal[b.id];
        })
        .reduce(
          (re, r) => {
            re[r.id.toString()] = r.name!;
            return re;
          },
          {} as Record<string, string>
        ),
    [filteredRows, idToOrdinal]
  );

  const onSelectionChange = useCallback(
    (type: 'lower' | 'upper') => (value: string) => {
      if (type === 'lower') {
        setLowerRowRange(value);
      } else {
        setUpperRowRange(value);
      }
    },
    []
  );

  const lowerDisabledValueOptions = useMemo(() => {
    if (!upperRowRange) {
      return undefined;
    }
    return Object.entries(valueOptionsContent).reduce(
      (res, [key, _]) => {
        const id = parseInt(key);
        const upperId = parseInt(upperRowRange);
        if (!isNaN(id) && !isNaN(upperId)) {
          res[key] = idToOrdinal[id] > idToOrdinal[upperId];
        }
        return res;
      },
      {} as Record<string, boolean>
    );
  }, [idToOrdinal, upperRowRange, valueOptionsContent]);

  const upperDisabledValueOptions = useMemo(() => {
    if (!lowerRowRange) {
      return undefined;
    }
    return Object.entries(valueOptionsContent).reduce(
      (res, [key, _]) => {
        const id = parseInt(key);
        const upperId = parseInt(lowerRowRange);
        if (!isNaN(id) && !isNaN(upperId)) {
          res[key] = idToOrdinal[id] <= idToOrdinal[upperId];
        }
        return res;
      },
      {} as Record<string, boolean>
    );
  }, [idToOrdinal, lowerRowRange, valueOptionsContent]);

  const readyToSave = useMemo(
    () =>
      lowerRowRange &&
      !isNaN(parseInt(lowerRowRange)) &&
      upperRowRange &&
      !isNaN(parseInt(upperRowRange)),
    [lowerRowRange, upperRowRange]
  );

  const onSaveRowRange = useCallback(() => {
    if (!lowerRowRange || !upperRowRange) {
      return;
    }
    const lowerRowId = parseInt(lowerRowRange);
    const upperRowId = parseInt(upperRowRange);
    if (isNaN(lowerRowId) || isNaN(upperRowId)) {
      return;
    }
    const lowerRowName = valueOptionsContent[lowerRowId];
    const upperRowName = valueOptionsContent[upperRowId];
    if (!lowerRowName || !upperRowName) {
      return null;
    }
    onUpdateRowRange(sectionId, lowerRowName, upperRowName);
  }, [
    lowerRowRange,
    onUpdateRowRange,
    sectionId,
    upperRowRange,
    valueOptionsContent,
  ]);

  const lowerDisplay = useMemo(() => {
    if (!lowerRowRange) {
      return undefined;
    }

    const lowerRowId = parseInt(lowerRowRange);
    if (isNaN(lowerRowId)) {
      return undefined;
    }

    return valueOptionsContent[lowerRowId];
  }, [lowerRowRange, valueOptionsContent]);
  const upperDisplay = useMemo(() => {
    if (!upperRowRange) {
      return undefined;
    }

    const upperRowId = parseInt(upperRowRange);
    if (isNaN(upperRowId)) {
      return undefined;
    }

    return valueOptionsContent[upperRowId];
  }, [upperRowRange, valueOptionsContent]);

  return (
    <Stack
      direction="column"
      gap="s"
      className={styles.inputContainer}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="spaceBetween"
        gap="l"
      >
        <Stack alignItems="center" gap="s">
          <Content id={ContentId.AbsoluteRowRange} />
          <TooltipPopover
            contentVariant={isDarkMode ? 'light' : 'dark'}
            triggerContent={<InfoOutlineIcon size={vars.iconSize.s} />}
          >
            <Content id={ContentId.AbsoluteRowRangeInfo} />
          </TooltipPopover>
        </Stack>
        {showInput ? (
          <FoldIcon
            onClick={(e) => {
              e.stopPropagation();
              setShowInput(false);
            }}
            size={vars.iconSize.s}
          />
        ) : (
          <ExpandIcon
            onClick={(e) => {
              e.stopPropagation();
              setShowInput(true);
            }}
            size={vars.iconSize.s}
          />
        )}
      </Stack>
      {showInput && (
        <Stack width="full" alignItems="center" gap="s">
          <PosSelect
            valueOptionsDisabled={lowerDisabledValueOptions}
            placeholderText={fromText}
            value={lowerRowRange}
            displayText={lowerDisplay}
            onChange={onSelectionChange('lower')}
            valueOptionsContent={valueOptionsContent}
          />
          {'->'}
          <PosSelect
            valueOptionsDisabled={upperDisabledValueOptions}
            placeholderText={toText}
            value={upperRowRange}
            displayText={upperDisplay}
            onChange={onSelectionChange('upper')}
            valueOptionsContent={valueOptionsContent}
          />
          <Button
            variant="text"
            disabled={!readyToSave}
            onClick={onSaveRowRange}
          >
            <CheckIcon size={vars.iconSize.s} />
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
